import * as React from "react";
import PropTypes from "prop-types";
import { withStyles, Typography } from "@material-ui/core";
import { CyNumber } from "../../utilities/number";
import moment from "moment";
import ResponsiveContainer from "recharts/es6/component/ResponsiveContainer";
import XAxis from "recharts/es6/cartesian/XAxis";
import YAxis from "recharts/es6/cartesian/YAxis";
import CartesianGrid from "recharts/es6/cartesian/CartesianGrid";
import Legend from "recharts/es6/component/Legend";
import Tooltip from "recharts/es6/component/Tooltip";
import { Bar, BarChart } from "recharts";
import CurrencyNumber from "../UI/CurrencyNumber";
import PercentageNumber from "../UI/PercentageNumber";

const styles = (theme) => ({
  wrapper: {
    position: "relative",
    width: "100%",
    breakInside: "avoid",
    "& tspan": {
      [`@media only screen and ${theme.breakpoints.downNoMedia("md")}`]: {
        fontSize: 12,
      },
    },
    "& .recharts-cartesian-axis-ticks": {
      fontSize: 14,
    },
    "& .legend-wrapper": {
      width: "20%",
      float: "right",
    },
  },
  chartSubtitle: {
    marginBottom: 20,
    textAlign: "center",
    [`@media only screen and ${theme.breakpoints.downNoMedia("md")}`]: {
      fontSize: 13,
    },
    "&.print": {
      textAlign: "left",
    },
  },
  label: {
    position: "absolute",
    top: 25,
    right: 50,
    backgroundColor: "white",
    border: `1px solid ${theme.palette.grey["A100"]}`,
    whiteSpace: "nowrap",
    padding: 10,
    zIndex: 99,
  },
  legend: {
    float: "right",
    marginTop: 14,
    "@media print": {
      float: "none",
      textAlign: "center",
    },
  },
  legendItem: {
    textAlign: "right",
    display: "block",
    position: "relative",
    color: theme.palette.grey[600],
    paddingRight: 44,
    fontSize: 14,
    "&.green": {
      color: theme.palette.primary.main,
    },
    "&:not(:first-child)": {
      marginLeft: 20,
    },
    [`@media only screen and ${theme.breakpoints.downNoMedia("md")}`]: {
      fontSize: 13,
    },
  },
  legendChip: {
    position: "absolute",
    display: "block",
    right: 0,
    top: 5,
    width: 33,
    height: 11,
    backgroundColor: theme.palette.grey[600],
    "-webkit-print-color-adjust": "exact",
    "&.green": {
      backgroundColor: theme.palette.primary.main,
    },
    "&.print": {
      position: "relative",
      display: "inline-block",
      verticalAlign: "top",
    },
  },
});

class CyTotalIncomeChart extends React.Component {
  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return (
      nextProps.data.incomeBreakdown[1].Cy !==
        this.props.data.incomeBreakdown[1].Cy ||
      nextProps.data.incomeBreakdown.length !==
        this.props.data.incomeBreakdown.length
    );
  }

  renderLegend = (props) => {
    const { payload } = props;
    const { classes, benchmarkName, strategyName } = this.props;
    return (
      <div className={classes.legend}>
        {payload.map((entry, index) => (
          <span
            className={`${classes.legendItem} ${
              entry.value === "Cy" ? "green" : ""
            }`}
            key={`item-${index}`}
          >
            <span
              className={`${classes.legendChip} ${
                entry.value === "Cy" ? "green" : ""
              }`}
            />
            {entry.value === "Cy" ? (
              <React.Fragment>{strategyName}</React.Fragment>
            ) : (
              <React.Fragment>{benchmarkName}</React.Fragment>
            )}
          </span>
        ))}
      </div>
    );
  };

  render() {
    const {
      data,
      classes,
      benchmarkName,
      initialInvestment,
      annualWithdrawal,
      strategyName,
      theme,
      renderForPdfOutput,
    } = this.props;
    const { CurrencyFormatter } = CyNumber;
    const chartData = data.incomeBreakdown.map((d) => {
      return { [benchmarkName]: d.Benchmark, Cy: d.Cy, year: d.year };
    });
    const chartHeight = 320;
    const ChartContainer = renderForPdfOutput ? "div" : ResponsiveContainer;

    return (
      <div className={classes.wrapper}>
        <Typography
          variant={"h5"}
          color={"primary"}
          align={renderForPdfOutput ? "left" : "center"}
        >
          Total Withdrawals
        </Typography>
        <Typography
          variant={"body1"}
          className={`${classes.chartSubtitle}  ${
            renderForPdfOutput && "print"
          }`}
        >
          <CurrencyNumber value={initialInvestment} /> Investment Amount{" "}
          <sup>&dagger;</sup>
          <br />
          <CurrencyNumber value={annualWithdrawal} /> Annual Income Withdrawals
          (<PercentageNumber value={data.inflation} /> Annual Upward Adjustment)
        </Typography>
        {!renderForPdfOutput && (
          <div style={{ textAlign: "right", paddingRight: 40 }}>
            <Typography variant={"body1"}>Total Withdrawals</Typography>
            <Typography variant={"body1"} color={"primary"}>
              {strategyName}&nbsp;&nbsp;
              <CurrencyNumber value={data.totalWithdrawal.Cy} />
            </Typography>
            <Typography
              variant={"body1"}
              style={{ color: theme.palette.grey[600] }}
            >
              {benchmarkName}&nbsp;&nbsp;
              <CurrencyNumber value={data.totalWithdrawal.Benchmark} />
            </Typography>
          </div>
        )}
        <ChartContainer
          {...(renderForPdfOutput
            ? { style: { width: "80%", float: "left" } }
            : { width: "100%", height: chartHeight })}
        >
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 40, left: 50, bottom: 30 }}
            {...(renderForPdfOutput
              ? {
                  width: 700,
                  height: 180,
                  margin: {},
                }
              : {})}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="year" />
            <YAxis tickFormatter={CurrencyFormatter} />
            <Tooltip
              cursor={false}
              formatter={CurrencyFormatter}
              itemSorter={() => 1}
            />
            {!renderForPdfOutput && <Legend content={this.renderLegend} />}
            <Bar
              dataKey="Cy"
              fill={theme.palette.primary.main}
              isAnimationActive={!renderForPdfOutput}
            />
            <Bar
              dataKey={benchmarkName}
              fill={theme.palette.grey[600]}
              isAnimationActive={!renderForPdfOutput}
            />
          </BarChart>
        </ChartContainer>
        {!renderForPdfOutput && (
          <Typography variant={"caption"} paragraph={true}>
            <sup>&dagger;</sup> Hypothetical{" "}
            <CurrencyNumber value={initialInvestment} /> investment. Income
            withdrawals taken quarterly. Source: Morningstar, Net of Fee,{" "}
            {moment().year()}.{" "}
            <i>Past performance does not guarantee future results.</i> Indices
            are unmanaged and cannot be invested indirectly.
          </Typography>
        )}
        {renderForPdfOutput && (
          <div className={"legend-wrapper"}>
            <div style={{ marginBottom: "0.2in" }}>
              <span className={`${classes.legendChip} green print`} />
              <Typography variant={"body2"}>
                {strategyName}
                <sup>*</sup>
                <br />
                (Total Withdrawals&nbsp;
                <CurrencyNumber value={data.totalWithdrawal.Cy} />)
              </Typography>
            </div>
            <div>
              <span className={`${classes.legendChip} print`} />
              <Typography variant={"body2"}>
                {benchmarkName}
                <sup>**</sup>
                <br />
                (Total Withdrawals&nbsp;
                <CurrencyNumber value={data.totalWithdrawal.Benchmark} />)
              </Typography>
            </div>
          </div>
        )}
      </div>
    );
  }
}

CyTotalIncomeChart.propTypes = {
  data: PropTypes.shape({
    totalWithdrawal: PropTypes.object.isRequired,
    incomeBreakdown: PropTypes.array.isRequired,
  }).isRequired,
  initialInvestment: PropTypes.number.isRequired,
  annualWithdrawal: PropTypes.number.isRequired,
  strategyName: PropTypes.string.isRequired,
  renderForPdfOutput: PropTypes.bool,
};

export default withStyles(styles, { withTheme: true })(CyTotalIncomeChart);



// WEBPACK FOOTER //
// ./src/components/Charts/CyTotalIncomeChart.jsx